import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import toast from 'react-hot-toast';
import {
  cancelMeeting,
  getScheduleContext,
  IsraelMeeting,
  IsraelScheduleContext,
} from '../services/israelScheduleService';
import SchedulingPanel, {
  BookedInfo,
  SchedulingMode,
} from '../components/israel/SchedulingPanel';
import { IL } from '../components/israel/israelTheme';
import CalendarDayIcon from '../components/israel/CalendarDayIcon';

// ---------------------------------------------------------------------------
// Formatting — render `start` (epoch ms, Israel-anchored) in Asia/Jerusalem.
// ---------------------------------------------------------------------------
function formatStartParts(epochMs: number): { date: string; time: string } {
  try {
    const d = new Date(epochMs);
    if (isNaN(d.getTime())) return { date: '', time: '' };
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Jerusalem',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const g = (t: string) => parts.find((p) => p.type === t)?.value || '';
    return {
      date: `${g('day')}.${g('month')}.${g('year')}`,
      time: `${g('hour')}:${g('minute')}`,
    };
  } catch {
    return { date: '', time: '' };
  }
}

// ---------------------------------------------------------------------------
// View model
// ---------------------------------------------------------------------------
type CardMode =
  | 'active-editable'
  | 'active-locked'
  | 'bookable'
  | 'blocked'
  | 'done';

interface MeetingView {
  key: string;
  number: number | null;
  therapistType: string;
  meetingType: string;
  start: { date: string; time: string } | null;
  text: string;
  mode: CardMode;
  isNextActive: boolean; // soonest upcoming active meeting
  raw: IsraelMeeting;
}

function toView(
  m: IsraelMeeting,
  index: number,
  isBookableSchedule: boolean
): MeetingView {
  let mode: CardMode;
  if (m.status === 'schedule') mode = isBookableSchedule ? 'bookable' : 'blocked';
  // future_schedule: grey box showing the API text, no red note (like blocked).
  else if (m.status === 'future_schedule') mode = 'blocked';
  else if (m.status === 'done') mode = 'done';
  else if (m.status === 'active' && m.can_update) mode = 'active-editable';
  else mode = 'active-locked';

  return {
    key: m.id != null ? `m-${m.id}` : `placeholder-${index}`,
    number: m.meeting_title_number,
    therapistType: m.therapist_type,
    meetingType: m.meeting_type,
    start: m.start != null ? formatStartParts(m.start) : null,
    text: m.text ?? '',
    mode,
    isNextActive: false,
    raw: m,
  };
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
function NumberedCalendarIcon({ number }: { number: number | null }) {
  return <CalendarDayIcon size={44} day={number ?? ''} numberColor={IL.green} />;
}

function SequenceArrow() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pt: 6 }}>
      {/* RTL: the timeline flows right→left, so the arrow points left */}
      <ArrowBackIcon sx={{ color: IL.dark }} />
    </Box>
  );
}

const PatientAvatarSvg: React.FC = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 68 68"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <mask
      id="patientAvatarMask"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="68"
      height="85"
    >
      <path
        d="M33.96 67.92C52.7156 67.92 67.92 52.7156 67.92 33.96C67.92 15.2044 52.7156 0 33.96 0C15.2044 0 0 15.2044 0 33.96C0 52.7156 15.2044 67.92 33.96 67.92Z"
        fill="white"
      />
    </mask>
    <g mask="url(#patientAvatarMask)">
      <path
        d="M42.9585 35.4844C47.926 30.5169 47.926 22.4631 42.9585 17.4956C37.9911 12.5281 29.9372 12.5281 24.9697 17.4956C20.0023 22.4631 20.0023 30.5169 24.9697 35.4844C29.9372 40.4519 37.991 40.4519 42.9585 35.4844Z"
        fill="#F39136"
      />
      <path
        d="M33.96 85.05C45.4586 85.05 54.78 75.7286 54.78 64.23C54.78 52.7315 45.4586 43.41 33.96 43.41C22.4615 43.41 13.14 52.7315 13.14 64.23C13.14 75.7286 22.4615 85.05 33.96 85.05Z"
        fill="#6EB2B5"
      />
    </g>
  </svg>
);

// Minimalist check-in-circle: circular outline (no fill) with a centered
// checkmark. Defaults to the booked (active) style — white circle + teal check;
// pass matching muted-gray colors for the done/inactive state.
const SuccessCheckIcon: React.FC<{
  size?: number;
  circleColor?: string;
  checkColor?: string;
  circleStrokeWidth?: number;
}> = ({
  size = 20,
  circleColor = '#FFFFFF',
  checkColor = '#18C58F',
  circleStrokeWidth = 5,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle
      cx="24"
      cy="24"
      r="19"
      stroke={circleColor}
      strokeWidth={circleStrokeWidth}
      fill="none"
    />
    <path
      d="M14 24 L20.5 30.5 L34 16.5"
      stroke={checkColor}
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

function PageHeader({ patientName }: { patientName?: string }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1.5, md: 0 },
        minHeight: 64,
        mb: 1,
      }}
    >
      {/* Mobile: logo stacked above the name. Desktop: pinned to the left. */}
      <Box
        component="img"
        src="/images/taliaz-logo.png"
        alt="Taliaz"
        sx={{
          position: { xs: 'static', md: 'absolute' },
          left: { md: 0 },
          height: { xs: 44, sm: 48, md: 36 },
          width: 'auto',
        }}
      />
      {patientName && (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: IL.white,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: 38, height: 38 }}>
              <PatientAvatarSvg />
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
            {patientName}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

interface MeetingCardProps {
  view: MeetingView;
  active: boolean;
  onEdit: (m: IsraelMeeting) => void;
  onCancel: (m: IsraelMeeting) => void;
  onBook: (m: IsraelMeeting) => void;
}

// A rounded status pill sitting under a card box (with a gap above it).
function StatusPill({
  label,
  color = '#F26B7B',
  onClick,
  overlap = false,
}: {
  label: string;
  color?: string;
  onClick?: () => void;
  overlap?: boolean;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        // `overlap` (mobile): sit over the bottom of the box above, narrower + centered.
        mt: overlap ? '-8px' : 1.5,
        width: overlap ? '80%' : undefined,
        mx: overlap ? 'auto' : undefined,
        position: overlap ? 'relative' : undefined,
        zIndex: overlap ? 2 : undefined,
        bgcolor: color,
        color: IL.white,
        border: overlap ? `2px solid ${IL.white}` : undefined,
        borderRadius: '20px',
        height: overlap ? 'auto' : 40,
        minHeight: overlap ? 32 : 40,
        py: overlap ? 0.75 : 0,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        fontSize: overlap ? 10 : 11.5,
        fontWeight: overlap ? 400 : 600,
        lineHeight: 1.2,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {label}
    </Box>
  );
}

// The state-dependent status box (shared by the desktop card + mobile row).
// `dense` shortens the single-line boxes for the tighter mobile rows.
function StateBox({
  view,
  active,
  onBook,
  dense = false,
}: {
  view: MeetingView;
  active: boolean;
  onBook: (m: IsraelMeeting) => void;
  dense?: boolean;
}) {
  const { mode } = view;
  const mh = dense ? 66 : 96;
  const radius = '20px';

  const filledBox = (opts: {
    fill: string;
    textColor: string;
    statusLabel: string;
    showCheck?: boolean;
    border?: string;
    checkCircleColor?: string;
    checkColor?: string;
    checkCircleStroke?: number;
  }) => (
    <Box
      sx={{
        width: '100%',
        minHeight: mh,
        borderRadius: radius,
        border: opts.border,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 0.25,
        px: 1,
        py: dense ? 0.75 : 1.25,
        background: opts.fill,
        color: opts.textColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {opts.showCheck !== false && (
          <SuccessCheckIcon
            size={20}
            circleColor={opts.checkCircleColor}
            checkColor={opts.checkColor}
            circleStrokeWidth={opts.checkCircleStroke}
          />
        )}
        <Typography sx={{ fontSize: dense ? 12 : 13, fontWeight: 600 }}>
          {opts.statusLabel}
        </Typography>
      </Box>
      {view.start &&
        (dense ? (
          <Typography dir="ltr" sx={{ fontWeight: 700, fontSize: 14 }}>
            {view.start.time} | {view.start.date}
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontWeight: 700 }}>{view.start.date}</Typography>
            <Typography sx={{ opacity: 0.7, lineHeight: 0.6 }}>—</Typography>
            <Typography sx={{ fontWeight: 700 }}>{view.start.time}</Typography>
          </>
        ))}
    </Box>
  );

  if (mode === 'bookable') {
    return (
      <Box
        role="button"
        tabIndex={0}
        aria-label="קביעת פגישה"
        onClick={() => onBook(view.raw)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onBook(view.raw);
        }}
        sx={{
          width: '100%',
          minHeight: mh,
          borderRadius: radius,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 1,
          cursor: 'pointer',
          // "schedule" box: green filled when its calendar is open. Idle state
          // is a green outline (no fill) on both web and mobile.
          bgcolor: active ? '#17C68C' : 'transparent',
          border: !active ? `1.5px solid #17C68C` : undefined,
          '&:hover': {
            bgcolor: active ? '#14B37E' : 'rgba(23,198,140,0.08)',
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: dense ? 500 : 400,
            fontSize: dense ? 14 : undefined,
            color: active ? IL.white : '#1F2937',
          }}
        >
          {active ? 'בחירת מועד הפגישה' : view.text || 'לחצ/י לקביעת פגישה'}
        </Typography>
        {view.raw.hint && (
          <Typography
            sx={{
              fontSize: dense ? 11.5 : 12,
              color: active ? IL.white : '#1F2937',
              opacity: 0.85,
              lineHeight: 1.3,
            }}
          >
            {view.raw.hint}
          </Typography>
        )}
      </Box>
    );
  }

  if (mode === 'blocked') {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: mh,
          borderRadius: radius,
          border: `1.5px solid ${IL.blueGrey}`,
          bgcolor: IL.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 1.5,
        }}
      >
        <Typography
          sx={{
            color: IL.muted,
            fontSize: dense ? 12.5 : 13,
            lineHeight: 1.35,
            // Server text may carry explicit line breaks (e.g. before "פגישה #N").
            whiteSpace: 'pre-line',
          }}
        >
          {view.text}
        </Typography>
      </Box>
    );
  }

  // Active meetings: the next (soonest) one is filled green with
  // "your next meeting"; every other active meeting is white + green border
  // with "meeting time". Editable vs locked only changes the actions below.
  if (mode === 'active-editable' || mode === 'active-locked') {
    return filledBox({
      fill: view.isNextActive ? '#0C576B' : '#20B2AA',
      textColor: IL.white,
      border: undefined,
      statusLabel: view.isNextActive ? 'הפגישה הבאה שלך:' : 'מועד הפגישה:',
      showCheck: false,
    });
  }

  if (mode === 'done') {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: mh,
          borderRadius: radius,
          border: `1.5px solid ${IL.blueGrey}`,
          bgcolor: IL.white,
          color: IL.blueGrey,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 0.25,
          px: 1,
          py: dense ? 0.75 : 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: dense ? 11 : 13, fontWeight: 600 }}>
            פגישה הסתיימה
          </Typography>
        </Box>
        {view.start &&
          (dense ? (
            <Typography dir="ltr" sx={{ fontWeight: 700, fontSize: 13 }}>
              {view.start.time} | {view.start.date}
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700 }}>{view.start.date}</Typography>
              <Typography sx={{ lineHeight: 0.6 }}>—</Typography>
              <Typography sx={{ fontWeight: 700 }}>{view.start.time}</Typography>
            </>
          ))}
      </Box>
    );
  }

  return null;
}

// Circular icon button used for the mobile booked-card actions (overlapping
// the box), with a white ring so it separates from the box behind it.
function RoundIconButton({
  ariaLabel,
  color,
  hoverColor,
  onClick,
  children,
}: {
  ariaLabel: string;
  color: string;
  hoverColor: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: color,
        border: `2px solid ${IL.white}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        '&:hover': { bgcolor: hoverColor },
      }}
    >
      {children}
    </Box>
  );
}

// Action row under a card (edit/cancel pills, or a status pill).
function MeetingActions({
  view,
  onEdit,
  onCancel,
  dense = false,
}: {
  view: MeetingView;
  onEdit: (m: IsraelMeeting) => void;
  onCancel: (m: IsraelMeeting) => void;
  dense?: boolean;
}) {
  const { mode } = view;
  return (
    <Box sx={{ width: '100%' }}>
      {mode === 'active-editable' &&
        (dense ? (
          // Mobile: round icon buttons overlapping the box's bottom corners
          // (green pencil on the right, red ✕ on the left).
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 1.5,
              mt: '-12px',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <RoundIconButton
              ariaLabel="עדכון"
              color="#17C68C"
              hoverColor={IL.greenDark}
              onClick={() => onEdit(view.raw)}
            >
              <EditOutlinedIcon sx={{ color: '#231F20', fontSize: 17 }} />
            </RoundIconButton>
            <RoundIconButton
              ariaLabel="ביטול"
              color={IL.red}
              hoverColor="#CF5A65"
              onClick={() => onCancel(view.raw)}
            >
              <CloseIcon sx={{ color: IL.white, fontSize: 17 }} />
            </RoundIconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 1.5 }}>
            <Button
              onClick={() => onEdit(view.raw)}
              fullWidth
              sx={{
                borderRadius: 5,
                height: 40,
                bgcolor: '#17C68C',
                color: '#231F20',
                '&:hover': { bgcolor: IL.greenDark },
              }}
            >
              עדכון
            </Button>
            <Button
              onClick={() => onCancel(view.raw)}
              fullWidth
              sx={{
                borderRadius: 5,
                height: 40,
                bgcolor: IL.red,
                color: IL.white,
                '&:hover': { bgcolor: '#CF5A65' },
              }}
            >
              ביטול
            </Button>
          </Box>
        ))}
      {/* Bottom red note: use the API `text` verbatim; hide it when empty. */}
      {mode === 'active-locked' && view.text && (
        <StatusPill label={view.text} overlap={dense} />
      )}
      {mode === 'done' && view.text && (
        <StatusPill label={view.text} overlap={dense} />
      )}
    </Box>
  );
}

// Desktop: vertical card in a horizontal timeline.
function MeetingCard({ view, active, onEdit, onCancel, onBook }: MeetingCardProps) {
  return (
    <Stack spacing={1} alignItems="center" sx={{ minWidth: 158, maxWidth: 172 }}>
      <NumberedCalendarIcon number={view.number} />

      <Box sx={{ textAlign: 'center', minHeight: 44 }}>
        <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {view.therapistType}
        </Typography>
        <Typography sx={{ color: IL.muted, lineHeight: 1.2 }}>
          {view.meetingType}
        </Typography>
      </Box>

      <StateBox view={view} active={active} onBook={onBook} />
      <MeetingActions view={view} onEdit={onEdit} onCancel={onCancel} />

      {/* Green pointer to the inline panel below */}
      {active && (
        <ArrowDropDownIcon sx={{ color: IL.green, fontSize: 40, mt: -0.5 }} />
      )}
    </Stack>
  );
}

// Mobile: a horizontal row — icon + label on the right, box + actions filling
// the left — stacked vertically with down-arrows between rows.
function MobileMeetingRow({
  view,
  active,
  onEdit,
  onCancel,
  onBook,
}: MeetingCardProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <NumberedCalendarIcon number={view.number} />
      </Box>
      <Box sx={{ width: 74, flexShrink: 0, textAlign: 'right' }}>
        <Typography sx={{ fontWeight: 400, lineHeight: 1.2, color: '#1F2937' }}>
          {view.therapistType}
        </Typography>
        <Typography sx={{ fontWeight: 400, lineHeight: 1.2, color: '#1F2937' }}>
          {view.meetingType}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StateBox view={view} active={active} onBook={onBook} dense />
        <MeetingActions view={view} onEdit={onEdit} onCancel={onCancel} dense />
        {active && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
            <ArrowDropDownIcon sx={{ color: IL.green, fontSize: 34 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// The vertical connector under the icon column, between mobile rows.
function MobileArrow() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Box sx={{ width: 52, display: 'flex', justifyContent: 'center', py: 0.5 }}>
        <ArrowDownwardIcon sx={{ color: IL.dark }} />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
interface ActivePanel {
  meeting: IsraelMeeting;
  mode: SchedulingMode;
}

function IsraelSchedulePage() {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // When embedded in the mobile app (?mobile_app=true), hide the Taliaz header
  // (logo + patient avatar/name) — the native app provides its own chrome.
  const hideHeader = useMemo(() => {
    const v = new URLSearchParams(window.location.search)
      .get('mobile_app')
      ?.toLowerCase();
    return v === 'true' || v === '1';
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [context, setContext] = useState<IsraelScheduleContext | null>(null);

  const [panel, setPanel] = useState<ActivePanel | null>(null);
  const [cancelTarget, setCancelTarget] = useState<IsraelMeeting | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Booking / reschedule confirmation popup (auto-closes after ~3s).
  const [confirmation, setConfirmation] = useState<
    | (BookedInfo & {
        therapistType: string;
        meetingType: string;
        kind: 'book' | 'reschedule';
      })
    | null
  >(null);
  // Cancellation-success popup (auto-closes after ~3s).
  const [cancelDone, setCancelDone] = useState<{
    therapistType: string;
    meetingType: string;
  } | null>(null);
  // True while the post-action background refresh is still running; if the
  // success popup closes before it lands, a spinner covers the stale strip.
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      setContext(await getScheduleContext(token));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Refetch the schedule without toggling the full-page loading state, so a
  // popup can stay visible while the strip updates behind it.
  const silentReload = useCallback(async () => {
    if (!token) return;
    try {
      setContext(await getScheduleContext(token));
    } catch {
      /* keep current data on failure */
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const views = useMemo<MeetingView[]>(() => {
    const meetings = context?.meetings ?? [];
    const firstScheduleIdx = meetings.findIndex((m) => m.status === 'schedule');
    const mapped = meetings.map((m, i) => toView(m, i, i === firstScheduleIdx));

    // Mark the soonest upcoming active meeting as "your next meeting".
    let nextKey: string | null = null;
    let minStart = Infinity;
    mapped.forEach((v) => {
      if (v.raw.status === 'active' && v.raw.start != null && v.raw.start < minStart) {
        minStart = v.raw.start;
        nextKey = v.key;
      }
    });
    return mapped.map((v) =>
      v.key === nextKey ? { ...v, isNextActive: true } : v
    );
  }, [context]);

  const activeKey = useMemo(() => {
    if (!panel) return null;
    return panel.meeting.id != null
      ? `m-${panel.meeting.id}`
      : `placeholder-${(context?.meetings ?? []).indexOf(panel.meeting)}`;
  }, [panel, context]);

  const handleSuccess = useCallback(
    (booked?: BookedInfo) => {
      const m = panel?.meeting;
      const mode = panel?.mode;
      setPanel(null);
      // Show the confirmation popup and refresh IMMEDIATELY in the background
      // (no full-page spinner). The popup hides once the refresh lands (with
      // a short minimum so it doesn't just flash).
      if (booked && m) {
        setConfirmation({
          ...booked,
          therapistType: m.therapist_type,
          meetingType: m.meeting_type,
          kind: mode === 'reschedule' ? 'reschedule' : 'book',
        });
        setRefreshing(true);
        const reload = silentReload().finally(() => setRefreshing(false));
        Promise.all([
          reload,
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]).finally(() => setConfirmation(null));
      } else {
        load();
      }
    },
    [panel, load, silentReload]
  );

  // Refresh already ran in the background when the popup opened; just close.
  const closeConfirmation = useCallback(() => {
    setConfirmation(null);
  }, []);

  // Auto-dismiss the booking confirmation after ~3s. Reschedule stays open
  // until the user closes it.
  useEffect(() => {
    if (!confirmation || confirmation.kind === 'reschedule') return;
    const t = setTimeout(() => closeConfirmation(), 3000);
    return () => clearTimeout(t);
  }, [confirmation, closeConfirmation]);

  const confirmCancel = async () => {
    if (!token || !cancelTarget?.id) return;
    const t = cancelTarget;
    setCancelling(true);
    try {
      const res = await cancelMeeting(token, t.id as number);
      if (!res?.ok) {
        toast.error('אירעה שגיאה, נסו שוב');
        return;
      }
      setCancelTarget(null);
      // Show the cancellation-success popup and refresh in the background;
      // hide the popup once the refresh lands (short minimum display).
      setCancelDone({
        therapistType: t.therapist_type,
        meetingType: t.meeting_type,
      });
      setRefreshing(true);
      const reload = silentReload().finally(() => setRefreshing(false));
      Promise.all([
        reload,
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]).finally(() => setCancelDone(null));
    } catch {
      toast.error('אירעה שגיאה, נסו שוב');
    } finally {
      setCancelling(false);
    }
  };

  // Refresh already ran in the background when the popup opened; just close.
  const closeCancelDone = useCallback(() => {
    setCancelDone(null);
  }, []);

  // Formatted time/date for the cancel popup (short "14.08.26" year).
  const cancelInfo = useMemo(() => {
    if (!cancelTarget || cancelTarget.start == null) return null;
    const p = formatStartParts(cancelTarget.start);
    const [d, m, y] = p.date.split('.');
    return { time: p.time, date: `${d}.${m}.${y.slice(2)}`, day: d };
  }, [cancelTarget]);

  const wrapper = (children: React.ReactNode) => (
    <Box sx={{ bgcolor: IL.pageBg, minHeight: '100vh' }}>
      <Container maxWidth={false} dir="rtl" sx={{ py: 4 }}>
        {children}
      </Container>
    </Box>
  );

  if (loading) {
    return wrapper(
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress />
        <Typography color="text.secondary">טוען...</Typography>
      </Box>
    );
  }

  if (error || !context) {
    return wrapper(
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          אופס
        </Typography>
        <Typography color="text.secondary">
          הקישור אינו תקין או שפג תוקפו
        </Typography>
      </Box>
    );
  }

  return wrapper(
    <Stack spacing={3}>
      {!hideHeader && <PageHeader patientName={context.patient_name} />}

      {/* Panel: teal frame + title band + white strip card */}
      <Box
        sx={{
          bgcolor: '#0C576B',
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: 22,
            color: IL.white,
            py: 2,
          }}
        >
          מערכת ניהול הפגישות שלי
        </Typography>
        <Box
          sx={{
            bgcolor: IL.white,
            borderRadius: '14px',
            mx: 1.5,
            mb: 1.5,
            p: 2.5,
            overflowX: isMobile ? 'visible' : 'auto',
          }}
        >
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {views.map((v, i) => {
                const rowActive = activeKey === v.key;
                // While a calendar is open (book or reschedule), dim + blur
                // every other row and its connector arrow so focus stays on the
                // meeting being scheduled.
                const panelOpen = panel != null && token != null;
                const dim = panelOpen && !rowActive;
                const dimSx = dim
                  ? {
                      filter: 'blur(3px)',
                      opacity: 0.45,
                      pointerEvents: 'none' as const,
                      userSelect: 'none' as const,
                    }
                  : {};
                return (
                  <React.Fragment key={v.key}>
                    <Box
                      sx={{ transition: 'filter .2s ease, opacity .2s ease', ...dimSx }}
                    >
                      <MobileMeetingRow
                        view={v}
                        active={rowActive}
                        onEdit={(m) => setPanel({ meeting: m, mode: 'reschedule' })}
                        onBook={(m) => setPanel({ meeting: m, mode: 'book' })}
                        onCancel={(m) => setCancelTarget(m)}
                      />
                    </Box>
                    {/* Calendar opens inline, right under the tapped meeting */}
                    {rowActive && panel && token && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        {/* key remounts the panel when switching meetings, so
                            the monthly view resets to the new target */}
                        <SchedulingPanel
                          key={`${activeKey}-${panel.mode}`}
                          token={token}
                          meeting={panel.meeting}
                          mode={panel.mode}
                          onSuccess={handleSuccess}
                          embedded
                        />
                      </Box>
                    )}
                    {i < views.length - 1 && (
                      <Box
                        sx={{
                          transition: 'filter .2s ease, opacity .2s ease',
                          ...(panelOpen
                            ? {
                                filter: 'blur(3px)',
                                opacity: 0.45,
                                pointerEvents: 'none' as const,
                                userSelect: 'none' as const,
                              }
                            : {}),
                        }}
                      >
                        <MobileArrow />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="center"
              sx={{ minWidth: 'min-content' }}
            >
              {views.map((v, i) => (
                <React.Fragment key={v.key}>
                  <MeetingCard
                    view={v}
                    active={activeKey === v.key}
                    onEdit={(m) => setPanel({ meeting: m, mode: 'reschedule' })}
                    onBook={(m) => setPanel({ meeting: m, mode: 'book' })}
                    onCancel={(m) => setCancelTarget(m)}
                  />
                  {i < views.length - 1 && <SequenceArrow />}
                </React.Fragment>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Desktop: scheduling panel below the strip. (Mobile renders it inline
          under the tapped meeting — see above.) */}
      {panel && token && !isMobile && (
        <SchedulingPanel
          key={`${activeKey}-${panel.mode}`}
          token={token}
          meeting={panel.meeting}
          mode={panel.mode}
          onSuccess={handleSuccess}
        />
      )}

      {/* Post-action refresh still running after the success popup closed -
          cover the stale strip with a spinner until the fresh data lands. */}
      {refreshing && !confirmation && !cancelDone && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
          }}
        >
          <CircularProgress sx={{ color: '#0C576B' }} />
        </Box>
      )}

      {/* Mobile booking-confirmation popup */}
      <Dialog
        open={!!confirmation}
        onClose={closeConfirmation}
        dir="rtl"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: `2px solid ${IL.green}`,
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        {confirmation && (
          <>
            {/* Green header with title + close */}
            <Box
              sx={{
                position: 'relative',
                bgcolor: IL.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ color: IL.white, fontWeight: 400, fontSize: 18 }}>
                {confirmation.kind === 'reschedule'
                  ? 'אישור שינוי פרטי פגישה!'
                  : 'פגישתך נקבעה!'}
              </Typography>
              <IconButton
                onClick={closeConfirmation}
                size="small"
                aria-label="סגירה"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: IL.white,
                  border: `2px solid ${IL.white}`,
                  p: '2px',
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Body: meeting title, then time + date */}
            <Box sx={{ bgcolor: IL.white, px: 3, py: 2.5 }}>
              {confirmation.kind === 'reschedule' && (
                <>
                  <Typography
                    sx={{ textAlign: 'center', color: '#1F2937', lineHeight: 1.5 }}
                  >
                    השינויים שביצעת נשמרו.
                    <br />
                    מועד הפגישה החדש:
                  </Typography>
                  <Divider sx={{ my: 2, width: '40%', mx: 'auto' }} />
                </>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  mb: 1,
                }}
              >
                <FolderOpenOutlinedIcon sx={{ fontSize: 36, color: '#1F2937' }} />
                <Typography sx={{ fontSize: 16, color: '#1F2937' }}>
                  פגישת {confirmation.therapistType} - {confirmation.meetingType}
                </Typography>
              </Box>
              {confirmation.kind !== 'reschedule' && <Divider sx={{ my: 2 }} />}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <CalendarDayIcon
                    size={38}
                    day={confirmation.day}
                    numberColor={IL.green}
                    strokeColor="#1F2937"
                  />
                  <Typography sx={{ fontWeight: 400, mt: 0.5 }} dir="ltr">
                    {confirmation.date}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 36, color: '#1F2937' }} />
                  <Typography sx={{ fontWeight: 400, mt: 0.5 }}>
                    {confirmation.time}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog
        open={cancelTarget != null}
        onClose={() => !cancelling && setCancelTarget(null)}
        dir="rtl"
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', m: 2 } }}
      >
        {cancelTarget && (
          <>
            {/* Pink header */}
            <Box sx={{ bgcolor: '#F26B7B', textAlign: 'center', py: 1.5, px: 2 }}>
              <Typography sx={{ color: IL.white, fontSize: 20 }}>
                ביטול פגישה?
              </Typography>
            </Box>

            {/* Body */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <FolderOpenOutlinedIcon sx={{ fontSize: 36, color: '#1F2937' }} />
                <Typography sx={{ fontSize: 16, color: '#1F2937' }}>
                  פגישת {cancelTarget.therapist_type} - {cancelTarget.meeting_type}
                </Typography>
              </Box>

              {cancelInfo && (
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#1F2937' }} dir="ltr">
                      {cancelInfo.date}
                    </Typography>
                    <CalendarDayIcon
                      size={34}
                      day={cancelInfo.day}
                      numberColor={IL.green}
                      strokeColor="#1F2937"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#1F2937' }}>
                      {cancelInfo.time}
                    </Typography>
                    <AccessTimeIcon sx={{ fontSize: 32, color: '#1F2937' }} />
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: 'rgba(31,41,55,0.2)' }} />

              <Typography sx={{ textAlign: 'center', color: '#1F2937', mb: 2 }}>
                האם לבטל את הפגישה?
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  onClick={() => setCancelTarget(null)}
                  disabled={cancelling}
                  sx={{
                    minWidth: 120,
                    borderRadius: 6,
                    py: 1,
                    bgcolor: '#6B7380',
                    color: IL.white,
                    '&:hover': { bgcolor: '#6B7380' },
                  }}
                >
                  לא
                </Button>
                <Button
                  onClick={confirmCancel}
                  disabled={cancelling}
                  sx={{
                    minWidth: 120,
                    borderRadius: 6,
                    py: 1,
                    bgcolor: '#F26B7B',
                    color: IL.white,
                    '&:hover': { bgcolor: '#F26B7B' },
                  }}
                >
                  {cancelling ? (
                    <CircularProgress size={20} sx={{ color: IL.white }} />
                  ) : (
                    'בטל פגישה'
                  )}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Dialog>

      {/* Cancellation-success popup */}
      <Dialog
        open={cancelDone != null}
        onClose={closeCancelDone}
        dir="rtl"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: `2px solid ${IL.green}`,
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        {cancelDone && (
          <>
            {/* Green header with title + close */}
            <Box
              sx={{
                position: 'relative',
                bgcolor: IL.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ color: IL.white, fontSize: 18 }}>
                אישור ביטול פגישה!
              </Typography>
              <IconButton
                onClick={closeCancelDone}
                size="small"
                aria-label="סגירה"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: IL.white,
                  border: `2px solid ${IL.white}`,
                  p: '2px',
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ bgcolor: IL.white, px: 3, pt: 5, pb: 6 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                }}
              >
                <CalendarDayIcon
                  size={40}
                  cancel
                  numberColor={IL.red}
                  strokeColor="#1F2937"
                />
                <Typography sx={{ fontSize: 16, color: '#1F2937' }}>
                  פגישת {cancelDone.therapistType} - {cancelDone.meetingType}
                </Typography>
              </Box>
              <Divider sx={{ my: 4, width: '40%', mx: 'auto' }} />
              <Typography sx={{ textAlign: 'center', color: '#1F2937' }}>
                הפגישה הוסרה מהיומן, יש לקבוע מועד חדש.
              </Typography>
            </Box>
          </>
        )}
      </Dialog>
    </Stack>
  );
}

export default IsraelSchedulePage;
